import { QueryErrorResetBoundary, useQuery } from '@tanstack/react-query';
import { LogoGenService } from '../services/logo-gen.api';
import LogoGenerateForm from '../components/LogoGenerateForm';
import { Skeleton } from 'components/ui/skeleton';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import Questions from 'features/bot-builder/components/Common/Questions';
import { Apps } from 'constants/global';

const GetStartedPage = () => {
  const userId = useSelector((state: RootState) => state.auth.user?.userId);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);

  const { data, isLoading, error } = useQuery({
    queryKey: ['get-logos', userId],
    queryFn: () => LogoGenService.getLogos(userId as string),
    enabled: !!userId,
  });

  if (error) {
    console.error('Error fetching logos:', error);
  }

  return (
    <main className="min-h-screen justify-center items-center bg-background-main">
      <div className="w-full h-full flex flex-col lg:flex-row justify-center bg-gradient-light dark:bg-gradient-dark bg-no-repeat bg-cover">
        <div className="w-full lg:w-[500px] lg:max-w-[500px] p-4 lg:p-[60px]">
          <div className="mb-6">
            <img
              src={isDarkMode ? '/logo-dark.png' : '/logo-light.png'}
              alt="Logo Generation"
              className="w-36 h-auto rounded-lg"
            />
          </div>
          <div className="text-text-primary space-y-5">
            <h1 className="text-2xl lg:text-[60px] font-medium leading-tight">
              Welcome to Logo Generator
            </h1>
            <p className="mt-4 text-sm lg:text-[20px] leading-relaxed">
              Create unique, professional logos effortlessly. From minimalist
              designs to bold statements, our tool helps you establish your
              brand identity!
            </p>
          </div>
        </div>

        <div className="w-full lg:w-3/5 lg:max-w-[800px] px-4 lg:px-[60px] pt-4 lg:pt-[60px] pb-6 lg:pb-[72px]">
          <div className="flex text-text-secondary mb-8">
            <div className="font-bold">
              <p className="text-xs lg:text-sm font-medium text-text-primary">
                START
              </p>
              <h3 className="text-xl lg:text-[28px] text-text-primary">
                Enter Logo Details
              </h3>
            </div>
          </div>

          <div className="bg-background rounded-lg p-4 lg:p-10">
            <LogoGenerateForm />
          </div>
        </div>
      </div>

      {/* Recent Logos Grid */}
      {isAuthenticated && (
        <div className="mt-8 px-4 text-text-primary">
          <h3 className="text-xl font-medium mb-4">
            Logos at a glance, get inspired!
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoading &&
              Array.from([1, 2, 3]).map((i) => (
                <div
                  key={`${i}-logo-generator-card`}
                  className="p-2 bg-background-main rounded-md"
                >
                  <div className="aspect-square">
                    <Skeleton className="w-full h-full" />
                  </div>
                </div>
              ))}

            {!isLoading &&
              data?.data?.logos &&
              data.data.logos.map((logo) => (
                <div
                  key={`${logo.id}-logo-generator-card`}
                  className="p-2 bg-background-card rounded-md"
                >
                  <img
                    src={logo.url}
                    className="rounded-md object-contain w-full h-64"
                    alt="Generated logo"
                  />
                </div>
              ))}
          </div>
        </div>
      )}

      <Questions appType={Apps.TEXT_TO_LOGO} />
    </main>
  );
};

export default GetStartedPage;
